import { useRouteError } from 'react-router-dom';

const RELOAD_KEY = 'chunk-reload';
const RELOAD_COOLDOWN_MS = 60 * 1000;

let isReloading = false;

/**
 * A module the browser cannot fetch almost always means the deploy it booted
 * from is gone, not that anything is broken. Reloading picks up the new entry
 * HTML and the error never needs to be shown.
 */
const isStaleDeployError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');

  return (
    /Failed to fetch dynamically imported module/.test(message) ||
    /error loading dynamically imported module/.test(message) ||
    /Importing a module script failed/.test(message)
  );
};

/**
 * Reloads at most once per cooldown, so a genuinely missing chunk cannot put
 * the tab into a refresh loop.
 * @return {boolean} Whether a reload was started.
 */
export const reloadForStaleDeploy = (): boolean => {
  if (isReloading) return true;

  const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;

  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  isReloading = true;
  window.location.reload();
  return true;
};

/**
 * Route-level error element. Without one, React Router renders its own
 * unstyled error screen, which is what users saw after every deploy.
 */
export default function AppErrorBoundary() {
  const error = useRouteError();

  // A cancelled vite:preloadError resolves the import with undefined, so
  // React.lazy throws its own error, unrelated to the stale chunk behind it.
  if (isReloading || (isStaleDeployError(error) && reloadForStaleDeploy())) {
    return null;
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <h1 className="text-xl font-medium text-foreground">
        Something went wrong
      </h1>
      <p className="max-w-md text-sm text-base-500">
        The page failed to load. Reloading usually fixes it. If it keeps
        happening, please let us know.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 dark:hover:bg-brand-400"
      >
        Reload
      </button>
    </div>
  );
}
